import React, { useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { getDestinationFaq } from '../../constants/destinationFaq';

const FaqItem = ({ id, title, content }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#fff',
        borderTop: open ? '7px solid #27306B' : '1px solid #e0e0e0',
        borderTopLeftRadius: open ? '12px' : 0,
        borderTopRightRadius: open ? '12px' : 0,
        overflow: 'hidden'
      }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={`${id}-content`}
        id={`${id}-header`}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          cursor: 'pointer',
          px: { xs: 2, md: 5 },
          py: { xs: 2, md: 2.5 },
          userSelect: 'none'
        }}>
        <Typography fontFamily="gothamBold" fontSize={{ xs: 12, md: 18 }} color="#000000">
          {title}
        </Typography>
        <ExpandMore
          sx={{
            color: 'rgba(0, 0, 0, 0.54)',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        />
      </Box>

      {open && (
        <Box
          id={`${id}-content`}
          role="region"
          aria-labelledby={`${id}-header`}
          sx={{ px: { xs: 2, md: 5 }, pb: 2.5 }}>
          <Typography fontFamily="gothamBook" fontSize={14} lineHeight="24px" color="#00000099">
            {content}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const DynamicFaqSection = ({ title }) => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const faqData = getDestinationFaq();

  if (!faqData.length) return null;

  let heading = 'Frequently Asked Questions';
  if (title && isLargeScreen) {
    heading = `${title} FAQ`;
  } else if (title) {
    heading = `${title} Frequently Asked Questions`;
  } else if (isLargeScreen) {
    heading = 'FAQ';
  }

  return (
    <Box
      id="faqSection"
      sx={{
        backgroundColor: '#f4f4f7',
        py: { xs: 4, md: 6 },
        scrollMarginTop: '100px'
      }}>
      <Box
        sx={{
          maxWidth: { xs: '100%', sm: '600px', md: '800px', lg: '1080px' },
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 }
        }}>
        <Typography
          component="h2"
          fontFamily="gothamBold"
          sx={{ pb: { xs: '22px', lg: '32px' } }}
          fontSize={{ xs: '18px', lg: '28px' }}
          lineHeight={{ xs: '26px', lg: '62px' }}
          textAlign={{ xs: 'left', lg: 'center' }}>
          {heading}
        </Typography>

        <Box
          className="accordion-container faq-accordion"
          sx={{
            width: '100%',
            backgroundColor: '#fff',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
          }}>
          {faqData.map((data, index) => (
            <FaqItem
              key={index}
              id={`destination-faq-${index}`}
              title={data.question}
              content={data.answer}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default DynamicFaqSection;
